import React, { useEffect, useState } from "react";
import styles from "./_user-management-page.module.scss";
import { useDialog } from "../../providers/DialogProvider";
import { useDrawer } from "../../providers/DrawerProvider";
import { useDispatch, useSelector } from "react-redux";

import {
  Organization,
  User,
  UserManagement,
  formatPhoneNumber,
  LoadingPanel,
  ReassignmentDialog,
  hasValue,
  AlertDialog,
  PermissionSet,
  PermissionAccessLevel,
  Client
} from "@amsanalytics/react-shared-library";

import { isNotNil } from "ramda";
import { AppState } from '../../store/rootReducer';
import { ILoggedInEmployee } from "../../Interfaces/Admin/ILoggedInEmployee";
import { isAdmin } from "../../utils/appUtils";
import { useConfiguration } from "../../Data/ConfigurationData";
import { userHasPermission } from "../../utils/utils";
import { permissions } from "../../utils/constants";
import { IEmployeeRole } from "../../Interfaces/Admin/IEmployeeRole";
import { createEmployee, createUser, deleteUser, loadAllUsers, revokeWithReassignment, resendUserInvite, reinstateUser, sendAppCenterLink, requestPasswordReset, updateUser, generateInvitationLink, clearInvitationLink } from "../../store/Admin/actions";
import { authService } from "../..";
import { httpGetAsync } from "../../utils/HttpClient";
import ConfirmationDialog from "../Dialogs/ConfirmationDialog";
import KendoConfirmationDialog from "../Dialogs/KendoConfirmationDialog";
import { Dialog as KendoDialog } from '@progress/kendo-react-dialogs';
import ErrorDialog from "../Dialogs/ErrorDialog";
import { AmsEmployee } from "../../Data/AmsEmployeeList";
import '../Dialogs/_kendo-confirmation-dialog.scss';

export default function UserManagementPage() {
  const [openDialog, closeDialog] = useDialog();
  const [openDrawer, closeDrawer] = useDrawer();
  const dispatch = useDispatch();
  // configuration data
  const { data: configuration } = useConfiguration();
  const isSuperUser: boolean = configuration?.isSuperUser || false;
  const users: User[] = useSelector((state: AppState) => state.admin.users);
  const currentUser: ILoggedInEmployee = useSelector((state: AppState) => state.applications.loggedInUser!.employee);
  const adUserIdDomains: string[] = useSelector((state: AppState) => state.applications.lookups.adUserIdDomains);
  const clients = useSelector<AppState, Client[]>((state: AppState) => state.applications.lookups.clients);
  const employeeRoles = useSelector<AppState, IEmployeeRole[]>((state: AppState) => state.applications.employeeRoles);
  const organizations = useSelector<AppState, Organization[]>((state: AppState) => state.applications.lookups.organizations);
  const loadingLabel: string | null = useSelector<AppState, string | null>((state: AppState) => state.admin.userManagementPageLoadingLabel);
  const invitationLinkUrl: string | null = useSelector<AppState, string | null>((state: AppState) => state.admin.invitationLinkUrl);
  const invitationLinkLoading: boolean = useSelector<AppState, boolean>((state: AppState) => state.admin.invitationLinkLoading);
  // Remembered locally so the invitation-link dialog can show who the link is for (resolved against the user list).
  const [pendingInviteAdUserId, setPendingInviteAdUserId] = useState<string | null>(null);
  const invitationLinkUser = pendingInviteAdUserId
    ? users.find(x => x.adUserId?.toLowerCase() === pendingInviteAdUserId.toLowerCase())
    : undefined;
  const invitationLinkFor: string | null = invitationLinkUser
    ? `${invitationLinkUser.firstName} ${invitationLinkUser.lastName} (${invitationLinkUser.adUserId})`
    : pendingInviteAdUserId;
  const [selectedClientId, setSelectedClientId] = useState<string>();
  const [gettingEmployeeAssignmentCount, setGettingEmployeeAssignmentCount] = useState(false);
  const [sigmaAccountTypes, setSigmaAccountTypes] = useState<string[]>([]);

  // Kendo dialog state for Revoke/Reinstate flows
  const [revokeDialog, setRevokeDialog] = useState<{ adUserId: string; firstName: string; lastName: string } | null>(null);
  const [reassignmentDialog, setReassignmentDialog] = useState<{ adUserId: string; employeeId: string; contactAssignmentCount: number } | null>(null);
  const [reinstateDialog, setReinstateDialog] = useState<{ adUserId: string; firstName: string; lastName: string } | null>(null);
  const [revokeErrorDialog, setRevokeErrorDialog] = useState(false);

  useEffect(() => {
    console.log('UserManagementPage mounted, fetching sigma account types...');
    const fetchSigmaAccountTypes = async () => {
      try {
        const accountTypes = await getSigmaAccountTypes();
        setSigmaAccountTypes(accountTypes);
      } catch (error) {
        console.error('Error in fetchSigmaAccountTypes:', error);
      }
    };
    
    // Only fetch if we have a current user (ensures clientId is available)
    if (currentUser) {
      fetchSigmaAccountTypes();
    }
  }, [currentUser]);

const getSigmaAccountTypes = async (): Promise<string[]> => {
  try {
    const bearerToken = await authService.getToken();
    console.log('🔵 [Sigma] Fetching account types...');
    
    const sigmaAccountTypes = await httpGetAsync(`api/sigma/getAccountTypeNames`, bearerToken);
    
    if (!sigmaAccountTypes) {
      console.warn('🔴 [Sigma] No response from sigma/getAccountTypeNames endpoint');
      return [];
    }

    console.log(`🔵 [Sigma] API Response Status: ${sigmaAccountTypes.status} ${sigmaAccountTypes.statusText}`);

    // Check if the API returned an error status
    if (!sigmaAccountTypes.ok) {
      console.error(`🔴 [Sigma] API error - Status ${sigmaAccountTypes.status}: ${sigmaAccountTypes.statusText}`);
      return [];
    }

    const result = await sigmaAccountTypes.json();
    console.log('🔵 [Sigma] Raw JSON response:', result);
    
    if (!Array.isArray(result)) {
      console.warn('🔴 [Sigma] Response is not an array:', result);
      return [];
    }

    if (result.length === 0) {
      console.warn('🟡 [Sigma] Empty account types list returned - check if Sigma API is configured in appsettings');
    } else {
      console.log(`✅ [Sigma] Successfully loaded ${result.length} account types:`, result);
    }

    return result;
  } catch (error) {
    console.error('🔴 [Sigma] Error fetching sigma account types:', error);
    return [];
  }
}
  useEffect(() => {
    if (currentUser && selectedClientId && isSuperUser) {
      dispatch(loadAllUsers(selectedClientId));
    } else if (currentUser && configuration && isNotNil(configuration?.clientId) && isAdmin(currentUser)) {
      dispatch(loadAllUsers(configuration!.clientId));
    }
  }, [currentUser, selectedClientId, configuration, isSuperUser]);

  // When a user is revoked, call httpGetAsync to get the assignment count for the employeeId
  const getContactAssignmentCount = async (employeeId: string) => {
    const bearerToken = await authService.getToken();
    const contactAssignmentCount: Response | undefined = await httpGetAsync(`api/Employee/allAssignmentCount/${employeeId}`, bearerToken);
    return await contactAssignmentCount?.json();
  }

  const handleRevokeUser = async (adUserId: string, employeeId: string) => {
    setGettingEmployeeAssignmentCount(true);
    const contactAssignmentCount = hasValue(employeeId) ? await getContactAssignmentCount(employeeId) : 0;
    setGettingEmployeeAssignmentCount(false);
    if (!contactAssignmentCount && contactAssignmentCount !== 0) {
      setRevokeErrorDialog(true);
      return;
    }
    const userToRevoke = users.find(user => user.adUserId.toUpperCase() === adUserId.toUpperCase());
    if (contactAssignmentCount > 0) {
      setReassignmentDialog({ adUserId, employeeId, contactAssignmentCount });
    } else {
      setRevokeDialog({
        adUserId,
        firstName: userToRevoke?.firstName || '',
        lastName: userToRevoke?.lastName || '',
      });
    }
  }

  // Only reached for inactive internal users — the shared grid routes guests to onResendUserInvite instead.
  const handleReinstateUser = (adUserId: string) => {
    const user = users.find(u => u.adUserId.toUpperCase() === adUserId.toUpperCase());
    setReinstateDialog({
      adUserId,
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
    });
  }

  const getValidateEmployeeNumberResult = async (employeeNumber: string) => {
    const bearerToken = await authService.getToken();
    const employeeNumberValidation: Response | undefined = await httpGetAsync(`api/ams/validateEmployeeNumber/${employeeNumber}`, bearerToken);
    return await employeeNumberValidation?.json();
  }

  const handleValidateEmployeeNumber = async (employeeNumber: string | undefined) => {
    if (employeeNumber) {
      const result = hasValue(employeeNumber) ? await getValidateEmployeeNumberResult(employeeNumber!) : null;

      if (result) {
        if (result.result != 'Employee not found') {
          openDialog({
            children: (
              <AlertDialog
              title="Employee Found"
               message={`Verify this is the employee name: ${result.result}`}
               onCancel={closeDialog}
              />
            ),
          });
        } else {
          openDialog({
            children: (
              <ErrorDialog
                title="Employee Not Found"
                errors={[`AMS Employee Number "${employeeNumber}" does not exist for this client.`]}
                closeDialog={() => {
                  closeDialog();
                }}
              />
            ),
          });
        }
      }
    }
  }


  const getAmsEmployeeListResult = async (firstName: string | undefined, lastName: string | undefined) => {
    const bearerToken = await authService.getToken();
    const employees: Response | undefined = await httpGetAsync(`api/ams/getAmsEmployee/${firstName}/${lastName}`, bearerToken);
    return await employees?.json();
  }

  const handleAmsEmployeeListResult = async (firstName: string | undefined, lastName: string | undefined) => {
    if (firstName && lastName) {
      const result = await getAmsEmployeeListResult(firstName, lastName);

      if (result && result.result) {
        // Format each employee as "FirstName LastName - amS2000Id"
        const formattedEmployees = result.result.map((employee: AmsEmployee) =>
          `${employee.firstName} ${employee.lastName} - ${employee.amS2000Id}`
        ).join('<br/>');

        openDialog({
          children: (
            <AlertDialog
              title="Employees Found"
              message={`${formattedEmployees}`}
              onCancel={closeDialog}
            />
          ),
        });
      }
    }
  }


  return (
    <>
      {currentUser && (
        <div className={styles.userManagementContainer}>
          {(loadingLabel || gettingEmployeeAssignmentCount) && (
            <LoadingPanel
            label={gettingEmployeeAssignmentCount ? 'Revoking...' : loadingLabel || 'Loading...'}
            style={{ position: 'absolute', top: '50%', left: '50%', zIndex: 1000, transform: 'translate(-50%, -50%)', backgroundColor: 'white', opacity: 0.8, width: '100%', height: '100%' }}
            />
          )}
          <UserManagement
            actions={{
              onActAsUser: (adUserId: string) => {
                // This feature is disabled for now in the LOS
              },
              onCreateUser: (user: User) => {
                dispatch(createUser(user));
              },
              onRevokeUser(adUserId: string, employeeId: string) {
                handleRevokeUser(adUserId, employeeId);
              },
              onReinstateUser: (adUserId: string) => {
                handleReinstateUser(adUserId);
              },
              onResendUserInvite: (adUserId: string) => {
                // Active guest => resend only; inactive guest => resend then reactivate (chained in the saga).
                const user = users.find(u => u.adUserId.toUpperCase() === adUserId.toUpperCase());
                dispatch(resendUserInvite(adUserId, !(user?.isActive ?? true)));
              },
              onCopyInvitationLink: (adUserId: string) => {
                setPendingInviteAdUserId(adUserId);
                dispatch(generateInvitationLink(adUserId));
              },
              onCloseInvitationLink: () => {
                setPendingInviteAdUserId(null);
                dispatch(clearInvitationLink());
              },
              onSendAppCenterLink: (adUserId: string) => {
                dispatch(sendAppCenterLink(adUserId));
              },
              onRequestPasswordReset: (adUserId: string) => {
                dispatch(requestPasswordReset(adUserId));
              },
              onResetPassword: (adUserId: string) => {
                // TODO: Implement in future once we establish a password reset flow with Azure.
              },
              onUpdateUser: (user: User) => {
                dispatch(updateUser(user));
              },
              onSelectClient: (clientId: string) => {
                setSelectedClientId(clientId);
              },
              onCreateEmployee: (employee: User) => {
                dispatch(createEmployee(employee));
              },
              onValidateEmployeeNumber: (employeeNumber: string | undefined) => {
                handleValidateEmployeeNumber(employeeNumber);
              },
              onGetEmployee: (firstName: string | undefined, lastName: string | undefined) => {
                handleAmsEmployeeListResult(firstName, lastName);
              }
            }}
            dialogDrawerProps={{
              openDrawer,
              closeDrawer,
              openDialog,
              closeDialog,
            }}
            flags={{
              isActingAsAnotherUser: false, // this feature is disabled for now in the LOS
              isAdmin: isSuperUser || isAdmin(currentUser),
              isSuperUser: isSuperUser,
              canActAsAnotherUser: false, // this feature is disabled for now in the LOS
              canEditUsers: isNotNil(currentUser) && userHasPermission(currentUser, permissions.users, PermissionSet.U, PermissionAccessLevel.All)
            }}
            gridProps={{
              disableAdding: isNotNil(currentUser) && !userHasPermission(currentUser, permissions.users, PermissionSet.C, PermissionAccessLevel.All),
              disableEditing: isNotNil(currentUser) && !userHasPermission(currentUser, permissions.users, PermissionSet.U, PermissionAccessLevel.All),
              disableFiltering: false,
              disableGrouping: true,
              disableSorting: false,
              disablePagination: false,
            }}
            lists={{
              clients,
              employeeRoles: employeeRoles.map((role: IEmployeeRole, idx: number) => {
                return {
                  id: role.id,
                  name: role.name,
                  sortOrder: idx,
                  description: role.description,
                  allowReportsTo: role.allowReportsTo,
                }
              }),
              organizations,
              adUserIdDomains,
              users: users?.map((user: User) => {
                return {
                  ...user,
                  organizationName: organizations.find(o => o.id === user.organizationId)?.name,
                  mobilePhone: user.mobilePhone ? formatPhoneNumber(user.mobilePhone) : '',
                  fullName: `${user.firstName} ${user.lastName}`,
                } as User
              }),
            }}
            strings={{
              clientIdForLoggedInUser: configuration?.clientId || '',
              selectedClientId
            }}
            sigmaAccountTypes={sigmaAccountTypes}
            invitationLinkUrl={invitationLinkUrl}
            invitationLinkFor={invitationLinkFor}
            invitationLinkLoading={invitationLinkLoading}
          />

          {/* Kendo Revoke Confirmation Dialog */}
          {revokeDialog && (
            <KendoConfirmationDialog
              title="Revoke User"
              message={`Are you sure you want to revoke the user ${revokeDialog.firstName} ${revokeDialog.lastName}?`}
              onCancel={() => setRevokeDialog(null)}
              onConfirm={() => {
                dispatch(deleteUser(revokeDialog.adUserId));
                setRevokeDialog(null);
              }}
            />
          )}

          {/* Kendo Reassignment Dialog */}
          {reassignmentDialog && (
            <KendoDialog title="Reassign Contacts" onClose={() => setReassignmentDialog(null)} width={450} className="kendo-confirmation-dialog">
              <ReassignmentDialog
                userToBeRevoked={users.find(user => user.adUserId.toUpperCase() === reassignmentDialog.adUserId.toUpperCase())!}
                users={users.filter(user => user.adUserId.toUpperCase() !== reassignmentDialog.adUserId.toUpperCase() && user.employeeId).sort((a, b) => a.lastName.localeCompare(b.lastName)).map(user => ({ id: user.employeeId!, name: user.firstName + ' ' + user.lastName }))}
                contactAssignmentCount={reassignmentDialog.contactAssignmentCount}
                onCancel={() => setReassignmentDialog(null)}
                onAccept={(toEmployeeIds: string[]) => {
                  dispatch(revokeWithReassignment(reassignmentDialog.adUserId, reassignmentDialog.employeeId, toEmployeeIds));
                  setReassignmentDialog(null);
                }}
              />
            </KendoDialog>
          )}

          {/* Kendo Reinstate Confirmation Dialog */}
          {reinstateDialog && (
            <KendoConfirmationDialog
              title="Confirm Reinstatement"
              message={`Are you sure you want to reinstate ${reinstateDialog.firstName} ${reinstateDialog.lastName}?`}
              onCancel={() => setReinstateDialog(null)}
              onConfirm={() => {
                dispatch(reinstateUser(reinstateDialog.adUserId));
                setReinstateDialog(null);
              }}
            />
          )}

          {/* Kendo Revoke Error Dialog */}
          {revokeErrorDialog && (
            <KendoConfirmationDialog
              title="Error"
              message="Unable to get contact assignment count. Please try again."
              onCancel={() => setRevokeErrorDialog(false)}
              onConfirm={() => setRevokeErrorDialog(false)}
              confirmText="OK"
              cancelText="Close"
            />
          )}
        </div>
      )}
    </>
  );
}
